<template>
  <div class="letters-tab">

    <!-- Evidence summary card -->
    <EvidenceSummaryCard
      v-if="listings.length > 0 || loadingListings"
      :listings="listings"
      :city-id="letterModel.cityId"
      @listings-changed="onListingsChanged"
    />

    <!-- Fallback evidence banner (no listings yet) -->
    <EvidenceStatus
      v-else-if="effectiveContext.evidence"
      :evidence="effectiveContext.evidence"
      :city-id="effectiveContext.cityId"
      :listing-id="effectiveContext.listingId"
      @updated="onEvidenceUpdated"
    />

    <!-- Toolbar -->
    <div class="letters-toolbar gap-2 justify-content-start align-items-center">
      <span class="section-title">{{ formatCount(letters.length, 'Letter') }}</span>
      <div v-if="cancelledCount > 0" class="form-check form-switch mb-0 small">
        <input class="form-check-input" type="checkbox" id="showCancelledToggle" v-model="showCancelled" />
        <label class="form-check-label letters-secondary text-muted" for="showCancelledToggle">
          Include voided ({{ cancelledCount }})
        </label>
      </div>
      <button
        class="btn btn-sm btn-outline-dark ms-auto d-flex align-items-center gap-1"
        @click="showNewLetterModal = true"
      >
        <i class="bi-plus-lg" /> New Letter
      </button>
    </div>

    <!-- Letter list -->
    <div class="d-flex flex-column" style="gap: 6px;">
      <CorrespondenceView
        v-for="letter in letters"
        :key="letter.id"
        :correspondence="letter"
        :context="effectiveContext"
        :listings="listings"
        @evidence-updated="onEvidenceUpdated"
        @listings-changed="onListingsChanged"
      />
    </div>

    <NewLetterModal
      v-model="showNewLetterModal"
      :print-options="letterModel.printOptions"
      :city-id="letterModel.cityId"
      :property-id="letterModel.property?.id"
      @create="addLetter"
    />
  </div>
</template>

<script>
import '@/assets/letters-tab.css';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/db';
import CorrespondenceView from "./CorrespondenceView.vue";
import NewLetterModal from "@/components/letters/NewLetterModal.vue";
import EvidenceStatus from "@/components/letters/EvidenceStatus.vue";
import EvidenceSummaryCard from "@/components/letters/EvidenceSummaryCard.vue";
import {Property} from "@/models/Property";
import {License} from "@/models/License";
import {buildLetterContext, resolveContextEvidence} from "@/models/letterContext";
import {fetchApi} from "@/api";

function mapListingDoc(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    url: data?.listing?.url || data?.url || null,
    source: data?.listing?.source || null,
    title: data?.listing?.title || null,
    iconUrl: data?.listing?.iconUrl || null,
    status: data?.status || 'active',
    evidence: data?.evidence ? {
      status: data.evidence.status,
      screenshotGcsPath: data.evidence.screenshotGcsPath || null,
      capturedAt: data.evidence.capturedAt || null
    } : null
  };
}

export default {
  components: {EvidenceSummaryCard, EvidenceStatus, NewLetterModal, CorrespondenceView},
  props: {
    property: Property,
    license: License
  },
  emits: ['close'],
  data() {
    return {
      showCancelled: false,
      showNewLetterModal: false,
      resolvedContext: null,
      listings: [],
      loadingListings: false,
      unsubscribeListings: null,
      listingsSubscriptionKey: null
    };
  },
  computed: {
    letterModel() {
      return buildLetterContext({
        property: this.property,
        license: this.license
      });
    },
    effectiveContext() {
      return this.resolvedContext || this.letterModel;
    },
    cancelledCount() {
      if (!this.property) return 0;
      return this.property.correspondences.filter(l => l.isCancelled).length;
    },
    letters() {
      if (!this.property) {
        return [];
      }
      return this.property.correspondences.filter(letter => this.showCancelled || !letter.isCancelled);
    }
  },
  watch: {
    letterModel: {
      immediate: true,
      handler(ctx) {
        if (ctx && !ctx.evidence && ctx.property?.id) {
          this.resolveEvidence();
        }
        if (ctx?.cityId && ctx?.property?.id) {
          this.startListingsListener(ctx.cityId, ctx.property.id);
        } else {
          this.stopListingsListener();
          this.listings = [];
          this.loadingListings = false;
        }
      }
    },
  },
  beforeUnmount() {
    this.stopListingsListener();
  },
  methods: {
    async addLetter({ template, options, listingId }) {
      if (!this.property) {
        return;
      }
      await this.property.addCorrespondence({ template, options, listingId });
    },
    async startListingsListener(cityId, propertyId) {
      if (!cityId || !propertyId) {
        this.stopListingsListener();
        this.listings = [];
        this.loadingListings = false;
        return;
      }

      const nextKey = `${cityId}:${propertyId}`;
      if (this.unsubscribeListings && this.listingsSubscriptionKey === nextKey) {
        return;
      }

      this.stopListingsListener();
      this.listingsSubscriptionKey = nextKey;
      this.loadingListings = true;

      const firestore = await db;
      if (this.listingsSubscriptionKey !== nextKey) {
        return;
      }

      const listingsQuery = query(
        collection(firestore, 'cities', cityId, 'listings'),
        where('verifiedPropertyId', '==', propertyId)
      );

      this.unsubscribeListings = onSnapshot(
        listingsQuery,
        (snapshot) => {
          if (this.listingsSubscriptionKey !== nextKey) {
            return;
          }
          this.listings = snapshot.docs.map(mapListingDoc);
          this.loadingListings = false;
        },
        async (err) => {
          if (this.listingsSubscriptionKey !== nextKey) {
            return;
          }
          console.error('Failed to subscribe to listings:', err);
          this.stopListingsListener();
          await this.fetchListings({ cityId, propertyId });
        }
      );
    },
    stopListingsListener() {
      if (this.unsubscribeListings) {
        this.unsubscribeListings();
        this.unsubscribeListings = null;
      }
      this.listingsSubscriptionKey = null;
    },
    async resolveEvidence() {
      this.resolvedContext = await resolveContextEvidence(this.letterModel);
    },
    async fetchListings({ cityId = this.letterModel?.cityId, propertyId = this.letterModel?.property?.id } = {}) {
      if (!cityId || !propertyId) return;
      this.loadingListings = true;
      try {
        const result = await fetchApi(`/v1/cities/${cityId}/properties/${propertyId}/listings`);
        this.listings = result?.listings || [];
      } catch (err) {
        console.error('Failed to fetch listings:', err);
      } finally {
        this.loadingListings = false;
      }
    },
    async onListingsChanged() {
      if (!this.unsubscribeListings) {
        await this.fetchListings();
      }
      await this.resolveEvidence();
    },
    async onEvidenceUpdated() {
      await this.resolveEvidence();
    }
  }
}
</script>
